import { createRouter, createWebHistory } from "vue-router";
import type { NavigationGuardNext, RouteLocationNormalized } from "vue-router";
import notAuth from "@/router/middleware/notAuth";
import auth from "@/router/middleware/auth";
import middlewarePipeline from "@/router/MiddlewarePipeline";
import claim from "@/router/middleware/claim";
import { enumClaims } from "@/common/claims/claimsEnum";
import { useAppStore } from "@/stores/app";
import { headerTitleMetaMap } from "@/common/headerTitleMetaMap";
import SuppliesView from "@/views/orders/supplies/SuppliesView.vue";
import BoxesView from "@/views/orders/boxes/BoxesView.vue";
import CombineProductsView from "@/views/goods/combineProducts/CombineProductsView.vue";
import CombineProducts from "@/components/product/combineProducts/CombineProducts.vue";

export interface MiddlewareContext {
  to: RouteLocationNormalized;
  from: RouteLocationNormalized;
  next: NavigationGuardNext;
  nextMiddleware?: () => void;
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/:pathMatch(.*)*",
      redirect: { name: "start" },
    },
    {
      path: "/",
      name: "start",
      component: () => import("@/views/BaseLayout.vue"),
      meta: {
        middleware: [auth],
      },
      children: [
        {
          path: "/main",
          name: "main",
          component: () => import("@/views/MainView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: enumClaims["Главная"],
          },
        },
        {
          path: "/products",
          name: "products",
          component: () => import("@/views/goods/products/ProductsView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: enumClaims["Каталог"],
          },
          redirect: { name: "productsList" },
          children: [
            {
              path: "/products/list",
              name: "productsList",
              component: () =>
                import("@/views/goods/products/ProductsListView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: enumClaims["Каталог"],
                titleInfo: headerTitleMetaMap.products,
              },
            },
            {
              path: "/products/create",
              name: "productCreate",
              component: () =>
                import("@/views/goods/products/ProductCreateView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: enumClaims["Каталог"],
                titleInfo: headerTitleMetaMap.addProduct,
              },
            },
            {
              path: "/products/card",
              name: "product",
              component: () => import("@/views/goods/products/ProductView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: enumClaims["Каталог"],
                titleInfo: headerTitleMetaMap.editProduct,
              },
            },
          ],
        },
        {
          path: "/categories",
          name: "categories",
          redirect: { name: "categoriesList" },
          meta: {
            middleware: [auth, claim],
            claim: enumClaims["Категории"],
          },
          component: () =>
            import("@/views/customCategories/CategoriesView.vue"),
          children: [
            {
              path: "/categories/list",
              name: "categoriesList",
              component: () =>
                import("@/views/customCategories/CategoriesListView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: enumClaims["Категории"],
                titleInfo: headerTitleMetaMap.categories,
              },
            },
            {
              path: "/categories/:id",
              name: "category",
              component: () =>
                import("@/views/customCategories/CategoryView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: enumClaims["Категории"],
                titleInfo: headerTitleMetaMap.editCategory,
              },
            },
            {
              path: "/categories/add",
              name: "categoriesAdd",
              component: () =>
                import("@/views/customCategories/CategoriesAddView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: enumClaims["Категории"],
                titleInfo: headerTitleMetaMap.addCategory,
              },
            },
          ],
        },
        {
          path: "/combine",
          name: "combineProducts",
          redirect: { name: "combineProductsBySize" },
          meta: {
            middleware: [auth, claim],
            claim: enumClaims["Объединение товаров"],
          },
          component: CombineProductsView,
          children: [
            {
              path: "/combine/size",
              name: "combineProductsBySize",
              component: CombineProducts,
              meta: {
                middleware: [auth, claim],
                claim: enumClaims["Объединение товаров"],
                titleInfo: headerTitleMetaMap.combineProducts,
              },
            },
          ],
        },
        {
          path: "/analytics",
          name: "analytics",
          component: () => import("@/views/AnalyticsView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: enumClaims["Аналитика"],
          },
        },
        {
          path: "/reviews",
          name: "reviews",
          component: () => import("@/views/ReviewsView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: enumClaims["Отзывы и сообщения"],
          },
        },
        {
          path: "/price-management",
          name: "priceManagement",
          component: () => import("@/views/PriceManagementView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: enumClaims["Управление ценами"],
          },
        },
        {
          path: "/warehouses",
          name: "warehouses",
          component: () =>
            import("@/views/warehouseAndRemains/warehouses/WarehousesView.vue"),
          redirect: { name: "warehousesList" },
          meta: {
            middleware: [auth, claim],
            claim: enumClaims["Склад и остатки"],
          },
          children: [
            {
              path: "/warehouses/list",
              name: "warehousesList",
              component: () =>
                import(
                  "@/views/warehouseAndRemains/warehouses/WarehouseListView.vue"
                ),
              meta: {
                middleware: [auth, claim],
                claim: enumClaims["Склад и остатки"],
                titleInfo: headerTitleMetaMap.warehouses,
              },
            },
            {
              path: "/warehouses/add",
              name: "warehouseAdd",
              component: () =>
                import(
                  "@/views/warehouseAndRemains/warehouses/WarehouseAddView.vue"
                ),
              meta: {
                middleware: [auth, claim],
                claim: enumClaims["Склад и остатки"],
                titleInfo: headerTitleMetaMap.addWarehouse,
              },
            },
            {
              path: "/warehouses/:warehouseId",
              name: "warehouse",
              component: () =>
                import(
                  "@/views/warehouseAndRemains/warehouses/WarehouseView.vue"
                ),
              meta: {
                middleware: [auth, claim],
                claim: enumClaims["Склад и остатки"],
                titleInfo: headerTitleMetaMap.editWarehouse,
              },
            },
          ],
        },
        {
          path: "/remains",
          name: "remainsParent",
          component: () =>
            import("@/views/warehouseAndRemains/remains/RemainsParentView.vue"),
          redirect: { name: "remainsTable" },
          meta: {
            middleware: [auth, claim],
          },
          children: [
            {
              path: "/remains/table",
              name: "remainsTable",
              component: () =>
                import(
                  "@/views/warehouseAndRemains/remains/RemainsTableView.vue"
                ),
              meta: {
                middleware: [auth, claim],
                titleInfo: headerTitleMetaMap.remains,
              },
            },
          ],
        },
        {
          path: "/orders",
          name: "orders",
          component: () => import("@/views/orders/OrdersView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: enumClaims["Заказы"],
          },
          children: [
            {
              path: "/orders/fbs/:status",
              name: "ordersFbs",
              component: () => import("@/views/orders/fbs/FbsView.vue"),
              meta: {
                middleware: [auth, claim],
                titleInfo: headerTitleMetaMap.fbsOrders,
                claim: enumClaims["Со своего склада FBS"],
              },
            },
            {
              path: "/orders/boxes",
              component: BoxesView,
              children: [
                {
                  path: "",
                  name: "boxes",
                  component: () =>
                    import("@/components/orders/boxes/Boxes.vue"),
                  meta: {
                    middleware: [auth, claim],
                    titleInfo: headerTitleMetaMap.boxes,
                    claim: enumClaims["Короба"],
                  },
                },
                {
                  path: ":boxId",
                  name: "boxSingle",
                  component: () =>
                    import("@/components/orders/boxes/boxSingle/BoxSingle.vue"),
                  meta: {
                    middleware: [auth, claim],
                    titleInfo: headerTitleMetaMap.boxesSingle,
                    claim: enumClaims["Поставки"],
                  },
                },
              ],
            },
            {
              path: "/orders/supplies",
              component: SuppliesView,
              children: [
                {
                  path: "",
                  name: "supplies",
                  component: () =>
                    import(
                      "@/components/orders/supplies/suppliesTable/SuppliesTable.vue"
                    ),
                  meta: {
                    middleware: [auth, claim],
                    titleInfo: headerTitleMetaMap.supplies,
                    claim: enumClaims["Поставки"],
                  },
                },
                {
                  path: ":supplyId",
                  name: "suppliesSingle",
                  component: () =>
                    import(
                      "@/components/orders/supplies/suppliesSingle/SuppliesSingle.vue"
                    ),
                  meta: {
                    middleware: [auth, claim],
                    titleInfo: headerTitleMetaMap.suppliesSingle,
                  },
                },
              ],
            },
            {
              path: "/orders/:assemblyTaskId",
              name: "assemblyTask",
              component: () => import("@/views/orders/OrderSingleView.vue"),
              meta: {
                middleware: [auth, claim],
                titleInfo: headerTitleMetaMap.orderSingle,
              },
            },
          ],
        },
        {
          path: "/settings",
          name: "settings",
          component: () => import("@/views/settings/SettingsView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: enumClaims["Настройки"],
          },
        },
        {
          path: "/organizations",
          name: "organizations",
          component: () => import("@/views/settings/OrganizationsView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: enumClaims["Организации"],
            titleInfo: headerTitleMetaMap.organizations,
          },
        },
        {
          path: "/integration",
          name: "integration",
          component: () => import("@/views/integration/IntegrationView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: enumClaims["Интеграции"],
          },
          redirect: { name: "integrationList" },
          children: [
            {
              path: "/integration/list",
              name: "integrationList",
              component: () =>
                import("@/views/integration/IntegrationListView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: enumClaims["Интеграции"],
                titleInfo: headerTitleMetaMap.integrations,
              },
            },
            {
              path: "/integration/:market",
              name: "integrationSettings",
              component: () =>
                import("@/views/integration/IntegrationSettings.vue"),
              meta: {
                middleware: [auth, claim],
                claim: enumClaims["Интеграции"],
                titleInfo: headerTitleMetaMap.integrations,
              },
            },
          ],
        },
        {
          path: "/staff",
          name: "staff",
          component: () => import("@/views/settings/staff/StaffView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: enumClaims["Сотрудники"],
          },
          redirect: { name: "staffList" },
          children: [
            {
              path: "/staff/list",
              name: "staffList",
              component: () =>
                import("@/views/settings/staff/StaffListView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: enumClaims["Сотрудники"],
                titleInfo: headerTitleMetaMap.staff,
              },
            },
            {
              path: "/staff/add",
              name: "staffUserAdd",
              component: () =>
                import("@/components/settings/staff/AddUser.vue"), // TODO
              meta: {
                middleware: [auth, claim],
                claim: enumClaims["Сотрудники"],
                titleInfo: headerTitleMetaMap.staffAdd,
              },
            },
            {
              path: "/staff/:id",
              name: "staffUser",
              component: () =>
                import("@/views/settings/staff/StaffUserView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: enumClaims["Сотрудники"],
                titleInfo: headerTitleMetaMap.staffAccess,
              },
            },
          ],
        },

        {
          path: "/labels",
          name: "labels",
          component: () => import("@/views/settings/labels/LabelsView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: enumClaims["Этикетки"],
            titleInfo: headerTitleMetaMap.labels,
          },
        },
        {
          path: "/labels/create",
          name: "labelsCreate",
          component: () =>
            import("@/views/settings/labels/LabelsCreateView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: enumClaims["Этикетки"],
            titleInfo: headerTitleMetaMap.addLabel,
          },
        },
        {
          path: "/labels/edit/:labelId",
          name: "labelsEdit",
          component: () =>
            import("@/views/settings/labels/LabelsCreateView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: enumClaims["Этикетки"],
            titleInfo: headerTitleMetaMap.editLabel,
          },
        },
        {
          path: "/partnerLogs",
          name: "partnerLogs",
          component: () =>
            import("@/views/settings/partnerLogs/PartnerLogsView.vue"),
          meta: {
            middleware: [auth, claim],
            titleInfo: headerTitleMetaMap.partnerLogs,
            claim: enumClaims["Логи"],
          },
        },
      ],
    },
    {
      path: "/enter",
      name: "enter",
      component: () => import("@/views/auth/enterPage/ui/EnterPage.vue"),
      meta: {
        middleware: [notAuth],
      },
    },
    {
      path: "/login",
      name: "login",
      component: () => import("@/views/auth/LoginView.vue"),
      meta: {
        middleware: [notAuth],
      },
    },
    {
      path: "/registration",
      name: "registration",
      component: () => import("@/views/auth/RegistrationView.vue"),
      meta: {
        middleware: [notAuth],
      },
    },
    {
      path: "/permissionDenied",
      name: "permissionDenied",
      component: () => import("@/views/PermissionDeniedView.vue"),
    },
    {
      path: "/test",
      name: "test",
      component: () => import("@/views/TestView.vue"),
    },
  ],
});

router.beforeEach((to, from, next) => {
  const appStore = useAppStore();

  // Проверка на несохранённые изменения
  if (appStore.hasNotSavedChanges) {
    if (from.name) {
      const confirmLeave = window.confirm(
        appStore.textForConfirm ||
          "У вас есть несохранённые изменения. Вы уверены, что хотите покинуть страницу?",
      );
      if (!confirmLeave) {
        return next(false); // Отмена перехода
      } else {
        appStore.resetNotSavedChanges();
      }
    }
  }

  // Если нет middleware
  if (!to.meta.middleware) {
    return next();
  }

  // Middleware обработка
  const middleware: any = to.meta.middleware;
  const context: MiddlewareContext = {
    to,
    from,
    next,
  };

  return middleware[0]({
    ...context,
    nextMiddleware: middlewarePipeline(context, middleware, 1),
  });
});

export default router;
